import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { GAS_METER } from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { EmptyActions } from "@/profitability/template-page/use-empty-actions";

export interface InsightsActions extends EmptyActions {
  /** Show the latest period, for a period on screen with none before it. */
  latest?: () => void;
}

/**
 * Insights before there are two periods to compare, with the step that gets
 * there: following the first period as it builds, moving to a later
 * period, or running one. Someone who may not run the agent, or one still
 * being set up (its builder is the page for whoever builds it), reads what it
 * is waiting for instead.
 */
export const InsightsStart = ({
  noun,
  periods,
  published,
  later,
  building,
  actions,
}: {
  noun: string;
  /** How many periods have run, through the one on screen: 0 or 1. */
  periods: number;
  /** False while the agent is still being set up. */
  published: boolean;
  /** The period on screen is not the latest: a later one already ran. */
  later: boolean;
  /** The first period is still running. */
  building: boolean;
  actions: InsightsActions;
}) => {
  const waiting = trendStart({
    noun,
    periods,
    published,
    later,
    building,
    actions,
  });
  return (
    <MachineEmptyState
      art={GAS_METER}
      layout="workspace"
      title={`Insights start with a second ${noun}`}
      description={waiting.description}
    >
      {waiting.action}
    </MachineEmptyState>
  );
};

const trendStart = ({
  noun,
  periods,
  published,
  later,
  building,
  actions,
}: {
  noun: string;
  periods: number;
  published: boolean;
  later: boolean;
  building: boolean;
  actions: InsightsActions;
}): { description: string; action?: ReactNode } => {
  if (!published)
    return {
      description: `This agent is still being set up. Once it has run two ${noun}s, this tab compares them.`,
    };
  if (later)
    return {
      description: `Insights end with the ${noun} on screen, and none ran before it. Pick a later ${noun} to compare.`,
      action: actions.latest && (
        <Button variant="secondary" onClick={actions.latest}>
          Go to the latest {noun}
        </Button>
      ),
    };
  if (building)
    return {
      description: actions.run
        ? `The first ${noun} is building. Once it finishes, run another and this tab compares them.`
        : `The first ${noun} is building. Once another ${noun} has run, this tab compares them.`,
      action: actions.progress && (
        <Button variant="secondary" onClick={actions.progress}>
          See progress
        </Button>
      ),
    };
  return {
    description: actions.run
      ? periods === 0
        ? `Run two ${noun}s and this tab compares them.`
        : `Run another ${noun} and this tab compares it with the first.`
      : periods === 0
        ? `Once this agent has run two ${noun}s, this tab compares them.`
        : `Once this agent runs another ${noun}, this tab compares it with the first.`,
    action: actions.run && <Button onClick={actions.run}>Run a period</Button>,
  };
};

type GapReason = "no_rollups" | "untracked_rollups";

/**
 * Why there is nothing to chart across periods, in the owner's terms, and
 * what Edit agent is handed to fix it: a report of company-wide totals is
 * broken down; one whose groups cannot be followed (no single name or
 * total, too many records, or marked untrackable) has its breakdown adjusted.
 */
const gapOf = (reason: GapReason, noun: string) =>
  reason === "untracked_rollups"
    ? {
        title: "This agent's groups can't be followed over time",
        note: `Charts follow each department, technician or other group ${noun} to ${noun}. This agent's groups can't be lined up across ${noun}s.`,
        fix: "adjust the breakdown",
        action: "Adjust the breakdown",
        request: `Insights can't follow this report's groups from ${noun} to ${noun}. Adjust the breakdown so each group has one name column and a total, has no more than 200 records, and can be followed over time.`,
      }
    : {
        title: "This agent only reports company-wide totals",
        note: "Charts need the report broken down by department or technician. This agent only reports company-wide totals.",
        fix: "break the report down",
        action: "Break it down",
        request: `Break the report down by department and by technician, with revenue, costs and gross profit for each, so Insights can compare them ${noun} to ${noun}.`,
      };

/**
 * Periods to compare and nothing to chart across them (`reason_code`), and
 * no check has caught anything. Said in the owner's terms, with the change
 * that fixes it.
 */
export const InsightsNothing = ({
  reason,
  noun,
  actions,
}: {
  reason: GapReason;
  noun: string;
  actions: InsightsActions;
}) => {
  const gap = gapOf(reason, noun);
  return (
    <MachineEmptyState
      art={GAS_METER}
      layout="workspace"
      title={gap.title}
      description={
        actions.change
          ? `Insights compare departments, technicians and other groups ${noun} to ${noun}. Ask Volca to ${gap.fix}, and the ${noun}s you run from then on chart here.`
          : `Insights compare departments, technicians and other groups ${noun} to ${noun}. Someone who manages this agent can ask Volca to ${gap.fix}.`
      }
    >
      {actions.change && (
        <Button onClick={() => actions.change?.(gap.request)}>
          {gap.action}
        </Button>
      )}
    </MachineEmptyState>
  );
};

/**
 * Above a rules table with no charts: the checks compare across periods,
 * but nothing else does (`reason_code`).
 */
export const InsightsBreakdownNote = ({
  reason,
  noun,
  actions,
}: {
  reason: GapReason;
  noun: string;
  actions: InsightsActions;
}) => {
  const gap = gapOf(reason, noun);
  return (
    <Stack
      direction="row"
      align="center"
      justify="between"
      gap={3}
      wrap
      className="rounded-lg border border-line px-4 py-3"
    >
      <Text tone="muted" className="min-w-0 flex-1 basis-80 text-pretty">
        {gap.note}
      </Text>
      {actions.change && (
        <Button
          variant="secondary"
          onClick={() => actions.change?.(gap.request)}
        >
          {gap.action}
        </Button>
      )}
    </Stack>
  );
};
